import { computed, inject, Injectable, signal } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
    BehaviorSubject,
    filter,
    forkJoin,
    map,
    mapTo,
    Observable,
    of,
    take,
    tap,
} from 'rxjs';
import { IRbacRole } from '@dxp/core/models';
import { UserLevel } from '@dxp/core/types';
import { decodeJwtPayload } from '@dxp/core/helpers';
import { RbacApiService } from '@dxp/core/api';

@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    readonly #rbacApiService = inject(RbacApiService);
    readonly #oidcSecurityService = inject(OidcSecurityService);

    readonly #role = signal<UserLevel>('user');
    readonly #permissions = signal<Map<string, Set<string>>>(new Map());
    readonly #permissionsLoaded$ = new BehaviorSubject<boolean>(false);
    public readonly permissionsLoaded$ =
        this.#permissionsLoaded$.asObservable();

    readonly group = signal<string>('');
    readonly role = computed<UserLevel>(() => this.#role());
    readonly permissions = computed<Map<string, Set<string>>>(() =>
        this.#permissions(),
    );

    waitForPermissions(): Observable<Map<string, Set<string>>> {
        return this.permissionsLoaded$.pipe(
            filter(loaded => loaded),
            take(1),
            map(() => this.#permissions()),
        );
    }

    loadPermissions(): Observable<void> {
        if (this.#permissionsLoaded$.getValue()) {
            return of(undefined);
        }

        return forkJoin({
            token: this.#oidcSecurityService.getAccessToken(),
            role: this.#rbacApiService.getPermissions(),
        }).pipe(
            tap(({ token, role }) => {
                this.#parseToken(token);
                this.#setPermissions(role);
            }),
            mapTo(undefined),
        );
    }

    hasAnyRole(roles: UserLevel[]): boolean {
        return roles.includes(this.#role());
    }

    hasAnyPermission(permissions: string[]): boolean {
        return permissions.some(permission => this.#hasPermission(permission));
    }

    hasAllPermissions(permissions: string[]): boolean {
        return permissions.every(permission => this.#hasPermission(permission));
    }

    clear() {
        this.#permissions.set(new Map());
        this.#permissionsLoaded$.next(false);
    }

    #parseToken(token: string) {
        const payload = decodeJwtPayload(token);
        this.#role.set(payload['buzzplus.role'] ?? 'user');
    }

    #setPermissions(role: IRbacRole): void {
        const map = new Map<string, Set<string>>();

        this.group.set(role.name);

        for (const permission of role.permissions) {
            const entity = permission.businessEntity.toLowerCase();
            const type = permission.permissionType.toLowerCase();

            if (!map.has(entity)) {
                map.set(entity, new Set());
            }
            map.get(entity)?.add(type);
        }

        this.#permissions.set(map);
        this.#permissionsLoaded$.next(true);
    }

    #hasPermissionByEntityAndType(entity: string, type: string): boolean {
        return this.#permissions().get(entity)?.has(type) ?? false;
    }

    #hasPermission(permission: string): boolean {
        const [entity, type] = permission.toLowerCase().split('.');
        return (
            !!entity &&
            !!type &&
            this.#hasPermissionByEntityAndType(entity, type)
        );
    }
}
